import { useState } from "react";
import { Link } from "react-router-dom";

import { LABEEB_AUDIT_DOMAINS } from "../../api/labeeb";
import {
  AUDIT_DOMAINS,
  actorEmail,
  auditStamp,
  auditTarget,
  auditValue,
} from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Reason } from "../../components/write/ReasonText";
import { Badge, Empty, FilterBar, Notice, Page, Segmented, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import {
  useAuditFeed,
  type AnyAuditDomain,
  type AnyAuditEvent,
  type AuditProduct,
} from "../../hooks/useAuditFeed";
import { ProductTag } from "../../products/PerProduct";
import { Button } from "../../f7";

/**
 * Where a target can be opened, when the console has a page for it.
 *
 * Product-dependent, because the same domain name means a different thing in
 * each: MCP's `quota` target is a tier-and-tool the caps page renders, while
 * Labeeb has no caps page at all. Linking a Labeeb row to `/policy/caps` would
 * send an operator to another product's screen and show them nothing.
 */
export function targetLink(
  e: { domain: string; target: unknown },
  product: AuditProduct,
): string | null {
  // A row with no target — or one carrying a non-string, which the server's
  // deserializer can emit — names nothing to open. It is not linkable, and it
  // is not a crash either; see the note on AuditEvent.target.
  const target = auditTarget(e.target);
  if (!target) return null;
  if (e.domain === "tenant") return `/tenants/${encodeURIComponent(target)}`;
  if (product === "mcp" && e.domain === "quota") {
    // A quota target is "<tier>" or "<tier>/<tool>"; the tier page covers both.
    return `/policy/caps/${encodeURIComponent(target.split("/")[0] ?? "")}`;
  }
  // Labeeb's config and flags rows name a key, and /policy/config renders the
  // whole set rather than one key, so there is nothing per-row to open.
  return null;
}

/**
 * The rows, defaulting to `[]`.
 *
 * Not mcp.ts's `eventsOf` — the feed now carries either product's rows, and
 * that helper is typed to MCP's narrower domain union.
 */
function rowsOf(p: { events?: AnyAuditEvent[] } | undefined | null): AnyAuditEvent[] {
  return p?.events ?? [];
}

/** The domains each product's audit table keys on. They are NOT the same set. */
export function domainsFor(product: AuditProduct): readonly string[] {
  return product === "labeeb" ? LABEEB_AUDIT_DOMAINS : AUDIT_DOMAINS;
}

/**
 * Who changed what, through this console.
 *
 * NOT merchant tool-call audit. Different table, different question, and the
 * API reference says explicitly never to merge the two into one panel — an
 * operator asking "who capped this tool" and one asking "what did this
 * merchant's assistant do" are looking for opposite things.
 *
 * This is also the ONLY durable record of two events. A blocklist row is
 * deleted on unblock, so nothing else remembers that someone was ever blocked
 * or who lifted it; a cap's `updated_by` stamp is overwritten on every change,
 * so nothing else remembers who set the previous value.
 */
export function Audit({ config }: { config: AppConfig }) {
  const [product, setProduct] = useState<AuditProduct>("mcp");
  const [domain, setDomain] = useState<AnyAuditDomain>("");
  const { state, reload, loadMore, loadingMore, moreError } = useAuditFeed(
    config,
    domain,
    product,
  );

  // The domain vocabularies do not overlap fully — `settings` is MCP's,
  // `flags` and `config` are Labeeb's. Carrying a filter across the switch
  // would send the other product a domain it answers 422 to, which reads as a
  // broken page rather than a filter that does not apply.
  function switchProduct(next: AuditProduct) {
    setProduct(next);
    setDomain("");
  }

  return (
    <Page
      title="Audit log"
      subtitle="Admin changes made through this console — not merchant tool calls."
    >

      {/* WHOSE LOG. Each product's admin API keeps its own audit table, so
          there is no single feed to show — the two paginate independently and
          key on different domains. This page used to read MCP's and carry a
          notice apologising for the other half; the switch is that notice
          turned into the feature. */}

      {/* One filter row, not a flex row wrapping another flex row. FilterBar
          already lays its children out on one line with the design's gap. */}
      <FilterBar>
        <Segmented
          value={product}
          onChange={switchProduct}
          label="Which product's audit log"
          options={[
            { value: "mcp", label: "MCP" },
            { value: "labeeb", label: "Labeeb" },
          ]}
        />
        <SelectField
          value={domain}
          onChange={(v) => setDomain(v as AnyAuditDomain)}
          label="Filter by domain"
          width={220}
        >
          <option value="">Everything, last 7 days</option>
          {domainsFor(product).map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </SelectField>
        <ProductTag id={product} />
        {!domain && (
          <span className="text-xs text-slate-500 dark:text-slate-400">
            The unfiltered view is windowed to 7 days; a domain filter is not.
          </span>
        )}
      </FilterBar>

      {state.kind === "loading" && <Loading what="the audit log" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the audit log" message={state.error.message} onRetry={reload} />
      )}
      {/* A 422 means the domain itself was rejected — "you asked for the wrong
          thing", never "nothing happened". Rendering it as an empty feed would
          report silence where there was a refusal. */}
      {state.kind === "invalid" && (
        <Notice tone="amber" title="That domain does not exist">
          {state.error.message} Valid domains are {AUDIT_DOMAINS.join(", ")}.
        </Notice>
      )}
      {state.kind === "notfound" && (
        <LoadFailed what="the audit log" message="Route not found on this gateway." />
      )}
      {state.kind === "error" && (
        <LoadFailed what="the audit log" message={state.error.message} />
      )}

      {/* `Load more` lives OUTSIDE the has-rows branch. A DynamoDB page can
          come back empty while later pages still hold rows — the cursor, not
          the row count, is what says whether there is more — and gating the
          button on `events.length > 0` stranded the feed on "No admin changes"
          with unread pages behind it. */}
      {state.kind === "ready" && (
        <>
          {rowsOf(state.data).length === 0 ? (
            <Empty>
              {domain ? `No ${domain} changes recorded.` : "No admin changes in the last 7 days."}
            </Empty>
          ) : (
          <Table
            head={
              <tr>
                <Th>When</Th>
                <Th>Who</Th>
                <Th>Action</Th>
                <Th>Target</Th>
                <Th>Change</Th>
                <Th>Why</Th>
              </tr>
            }
          >
            {rowsOf(state.data).map((e: AnyAuditEvent, i: number) => {
              const to = targetLink(e, product);
              const target = auditTarget(e.target);
              return (
                <tr key={`${e.timestamp}-${i}`}>
                  <Td>
                    <span
                      className="text-xs text-slate-500 dark:text-slate-400"
                      title={e.timestamp ?? undefined}
                    >
                      {auditStamp(e.timestamp)}
                    </span>
                  </Td>
                  <Td>{actorEmail(e.actor)}</Td>
                  <Td>
                    <Badge>{e.action}</Badge>
                    <span className="ml-1.5 text-xs text-slate-400 dark:text-slate-500">{e.domain}</span>
                  </Td>
                  <Td>
                    {to ? (
                      <Link to={to} className="ref">
                        {target}
                      </Link>
                    ) : target ? (
                      <span className="ref">{target}</span>
                    ) : (
                      // An empty cell would read as "changed nothing"; this row
                      // did change something and did not record what.
                      <span className="text-slate-400 dark:text-slate-500">not recorded</span>
                    )}
                  </Td>
                  <Td>
                    <span className="text-xs">
                      <span className="text-slate-500 dark:text-slate-400">{auditValue(e.before)}</span>
                      <span className="mx-1 text-slate-400 dark:text-slate-500">→</span>
                      <span>{auditValue(e.after)}</span>
                    </span>
                  </Td>
                  <Td>
                    {/* THE REASON THE OPERATOR TYPED. Both APIs store it and
                        both returned it all along; neither `AuditEvent` nor
                        `LabeebAuditEvent` declared the field, so the console
                        collected a mandatory 10-character justification on
                        every write and then threw it away — leaving the audit
                        log showing who/what/when and never WHY, which is the
                        one part a reader six months later needs.
                        Through `Reason` so a ticket key becomes a link, and
                        so the text renders as text and never as markup. */}
                    {e.reason ? (
                      <span className="text-xs">
                        <Reason text={e.reason} />
                      </span>
                    ) : (
                      // Not an empty cell: "nobody gave one" and "this row
                      // predates the field" both read as a blank otherwise.
                      <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
                    )}
                  </Td>
                </tr>
              );
            })}
          </Table>
          )}

          {moreError && (
            <p className="mt-4 text-xs text-amber-700 dark:text-amber-400">{moreError}</p>
          )}
          {state.data.cursor && (
            <div className="mt-4">
              <Button outline disabled={loadingMore} onClick={loadMore}>
                {loadingMore ? "Loading…" : "Load more"}
              </Button>
            </div>
          )}
        </>
      )}
    </Page>
  );
}
